import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';

import { Company } from '@core/models/company';
import { User } from '@core/models/user';

import { CompanyService } from '@core/services/company.service';
import { CompanyStateService } from '@core/services/company-state.service';
import { AuthFlowService } from '@core/services/auth-flow.service';
import { AuthService } from '@core/services/auth.service';
import { UserService } from '@core/services/user.service';
import { ThemeService } from '@core/services/theme.service';
import { ProfileValidationService } from '@core/services/profile-validation.service';

/**
 * ScanningConfigComponent
 *
 * Collects technical scanning preferences and configuration for TRIAL/PREMIUM tier users.
 * This component helps configure evaluation accounts with appropriate scanning settings
 * based on the organization's network environment and security requirements.
 */
@Component({
  selector: 'app-scanning-config',
  templateUrl: './scanning-config.component.html',
  styleUrl: './scanning-config.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, ReactiveFormsModule],
})
export class ScanningConfigComponent implements OnInit {
  private authService = inject(AuthService);
  private authFlowService = inject(AuthFlowService);
  private fb = inject(FormBuilder);
  private companyService = inject(CompanyService);
  private companyStateService = inject(CompanyStateService);
  private userService = inject(UserService);
  private themeService = inject(ThemeService);
  private profileValidationService = inject(ProfileValidationService);
  private router = inject(Router);

  scanningConfigForm!: FormGroup;
  user?: User;
  company?: Company;
  loading = false;
  serverMessage?: string;

  // Tier information
  userTier: 'free' | 'trial' | 'premium' = 'trial';
  isTrialOrPremium = true;

  // Configuration options
  networkTypeOptions = [
    {
      value: 'internal',
      label: 'Internal Network Only',
      description: 'Scan internal corporate networks and systems',
    },
    {
      value: 'external',
      label: 'External/Public Assets',
      description: 'Scan public-facing websites and services',
    },
    {
      value: 'hybrid',
      label: 'Both Internal & External',
      description: 'Comprehensive scanning of all assets',
    },
    {
      value: 'cloud',
      label: 'Cloud Infrastructure',
      description: 'AWS, Azure, GCP cloud environments',
    },
  ];

  scanFrequencyOptions = [
    {
      value: 'daily',
      label: 'Daily',
      description: 'High-frequency monitoring for critical environments',
    },
    {
      value: 'weekly',
      label: 'Weekly',
      description: 'Regular scanning for most organizations',
    },
    {
      value: 'monthly',
      label: 'Monthly',
      description: 'Periodic assessment for stable environments',
    },
    {
      value: 'on-demand',
      label: 'On-Demand Only',
      description: 'Manual scans when needed',
    },
  ];

  alertPreferenceOptions = [
    {
      value: 'critical',
      label: 'Critical Vulnerabilities Only',
      description: 'High-impact security issues',
    },
    {
      value: 'high-critical',
      label: 'High & Critical',
      description: 'Significant security findings',
    },
    {
      value: 'all-severity',
      label: 'All Severity Levels',
      description: 'Comprehensive vulnerability reporting',
    },
    {
      value: 'custom',
      label: 'Custom Threshold',
      description: 'Define specific criteria',
    },
  ];

  integrationTypeOptions = [
    {
      value: 'api',
      label: 'API Integration',
      description: 'Programmatic access and automation',
    },
    {
      value: 'email',
      label: 'Email Reports Only',
      description: 'Periodic email summaries',
    },
    {
      value: 'dashboard',
      label: 'Web Dashboard Only',
      description: 'Manual review via portal',
    },
    {
      value: 'siem',
      label: 'SIEM Integration',
      description: 'Security information and event management',
    },
  ];

  constructor() {
    this.themeService.setSelectPlanPageLayout(true);
    this.initializeComponent();
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  /**
   * Initialize component data and user information
   */
  private async initializeComponent(): Promise<void> {
    this.themeService.showSpinner();

    try {
      const user = await this.authService.waitForHydratedUser(10000);
      if (!user) {
        await this.router.navigate(['/login'], { replaceUrl: true });
        return;
      }

      this.user = user;
      this.userTier = user.intendedTier || 'trial';
      this.isTrialOrPremium =
        this.userTier === 'trial' || this.userTier === 'premium';

      if (!this.isTrialOrPremium) {
        const nextRoute = await this.authFlowService.getPostLoginRoute(user);
        await this.router.navigateByUrl(nextRoute, { replaceUrl: true });
        return;
      }

      if (user.currentCompanyId) {
        try {
          const company = await this.companyService.getCompany(user.currentCompanyId);
          if (company) {
            this.company = company;
          }
        } catch (error) {
          console.error('Error loading company:', error);
        }
      }

      this.themeService.hideSpinner();
    } catch (error) {
      console.error('Error initializing component:', error);
      this.serverMessage = 'Error loading configuration. Please try again.';
      this.themeService.hideSpinner();
    }
  }

  /**
   * Initialize the scanning configuration form
   */
  private initializeForm(): void {
    this.scanningConfigForm = this.fb.group({
      networkType: ['', [Validators.required]],
      scanFrequency: ['', [Validators.required]],
      alertLevel: ['', [Validators.required]],
      notificationMethod: ['', [Validators.required]],
      integrationType: [''],
      complianceRequirements: [''],
      additionalRequirements: [''],
    });
  }

  /**
   * Handle form submission to save scanning configuration
   */
  async onSubmit(): Promise<void> {
    if (!this.scanningConfigForm.valid || !this.user) {
      this.markFormGroupTouched();
      return;
    }

    this.loading = true;
    this.serverMessage = undefined;
    this.themeService.showSpinner();

    try {
      const configData = this.scanningConfigForm.value;

      // Update company with scanning configuration using CompanyStateService
      if (this.company) {
        const scanningConfig = {
          networkType: configData.networkType,
          scanFrequency: configData.scanFrequency,
          alertLevel: configData.alertLevel,
          notificationMethod: configData.notificationMethod,
          integrationType: configData.integrationType || '',
          complianceRequirements: configData.complianceRequirements || '',
          additionalRequirements: configData.additionalRequirements || '',
        };

        // Initialize CompanyStateService with the current company
        this.companyStateService.setCurrentCompany(this.company);

        this.company = await this.companyStateService.updateSettings({ scanningConfig });
      }

      // Update user scanning configuration completion
      this.user.scanningConfigCompleted = true;
      this.user.updatedAt = new Date().toISOString();

      await this.userService.update(this.user, this.user.uid);

      // Update local models
      this.userService.setUserModel(this.user);
      if (this.company) {
        this.companyService.company = this.company;
      }

      this.loading = false;
      this.themeService.hideSpinner();

      // Proceed to next step
      this.proceedToNextStep();
    } catch (error) {
      console.error('Error saving scanning configuration:', error);
      this.serverMessage = 'Error saving configuration. Please try again.';
      this.loading = false;
      this.themeService.hideSpinner();
    }
  }

  /**
   * Skip scanning configuration (allow minimal setup)
   */
  onSkip(): void {
    if (!this.user) return;

    // Mark as completed with minimal config
    this.user.scanningConfigCompleted = true;
    this.userService.update(this.user, this.user.uid);
    this.userService.setUserModel(this.user);

    this.proceedToNextStep();
  }

  /**
   * Proceed to the next step in the registration flow
   */
  proceedToNextStep(): void {
    if (!this.user) return;

    void this.authFlowService
      .getPostLoginRoute(this.user)
      .then((nextRoute) => this.router.navigateByUrl(nextRoute, { replaceUrl: true }));
  }

  /**
   * Mark all form fields as touched to show validation errors
   */
  private markFormGroupTouched(): void {
    Object.keys(this.scanningConfigForm.controls).forEach((field) => {
      const control = this.scanningConfigForm.get(field);
      control?.markAsTouched({ onlySelf: true });
    });
  }

  /**
   * Check if a field is invalid and should show error
   */
  isFieldInvalid(fieldName: string): boolean {
    const field = this.scanningConfigForm.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  /**
   * Get error message for a field
   */
  getFieldError(fieldName: string): string {
    const field = this.scanningConfigForm.get(fieldName);
    if (field?.errors && (field.dirty || field.touched)) {
      if (field.errors['required']) {
        const fieldDisplayNames: { [key: string]: string } = {
          networkType: 'Network type',
          scanFrequency: 'Scan frequency',
          alertPreferences: 'Alert preferences',
          integrationType: 'Integration type',
        };
        const displayName = fieldDisplayNames[fieldName] || fieldName;
        return `${displayName} is required for ${this.userTier} tier configuration`;
      }
    }
    return '';
  }

  /**
   * Get explanation for tier requirements
   */
  getTierRequirementText(): string {
    return `${this.userTier.toUpperCase()} tier requires scanning configuration to set up your evaluation account with appropriate settings.`;
  }

  // Additional properties and methods needed by template
  tierInfo = {
    tierName: 'trial',
    description: 'Enhanced features with technical scanning configuration',
  };

  isLoading = false;
  errorMessage = '';

  /**
   * Get tier badge CSS class
   */
  getTierBadgeClass(): string {
    switch (this.userTier) {
      case 'trial':
        return 'bg-warning';
      case 'premium':
        return 'bg-primary';
      default:
        return 'bg-success';
    }
  }

  /**
   * Get tier icon class
   */
  getTierIcon(): string {
    switch (this.userTier) {
      case 'trial':
        return 'bi-clock-fill';
      case 'premium':
        return 'bi-star-fill';
      default:
        return 'bi-check-circle-fill';
    }
  }

  /**
   * Navigate back to company setup
   */
  goBack(): void {
    this.router.navigate(['/register/company']);
  }

  /**
   * Save configuration as draft
   */
  async saveAsDraft(): Promise<void> {
    if (this.user) {
      // Save form data to local storage or user notes for draft save
      this.user.updatedAt = new Date().toISOString();
      await this.userService.update(this.user, this.user.uid);

      // Show success message
      this.serverMessage = 'Configuration saved as draft.';
    }
  }
}
